import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DateRange } from "react-day-picker";
import { Card, CardContent } from "@/components/ui/card";
import { useQueryClient } from "@tanstack/react-query";
import { opportunityKeys } from "@/hooks/queries/useOpportunities";
import { Badge } from "@/components/ui/badge";
import { OpportunityKanban } from "@/components/opportunities/OpportunityKanban";
import { OpportunityTable } from "@/components/opportunities/OpportunityTable";
import { OpportunityDetail } from "@/components/opportunities/OpportunityDetail";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreateOpportunityModal } from "@/components/opportunities/CreateOpportunityModal";
import { EditOpportunityModal } from "@/components/opportunities/EditOpportunityModal";
import { CreateStageModal } from "@/components/opportunities/CreateStageModal";
import { OpportunitySourcesModal } from "@/components/opportunities/OpportunitySourcesModal";
import { LoadingState } from "@/components/ui/loading-state";
import { Opportunity } from "@/services/opportunityService";
import { OpportunityStage } from "@/services/opportunityService";
import { Pipeline } from "@/types/pipeline";
import {
  Plus,
  BarChart3,
  Target,
  Loader2,
  AlertCircle,
  List,
  Settings,
  LayoutGrid,
  LayoutList,
  Eye,
  EyeOff,
  Filter,
  Trophy,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { usePipelinePermissions } from "@/hooks/usePipelinePermissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { contactoAPI } from "@/services/contactoService";
import { companyAPI } from "@/services/companyService";
import { opportunityAPI } from "@/services/opportunityService";
import { opportunitySourcesAPI } from "@/services/opportunitySourcesService";
import { currencyAPI } from "@/services/currencyService";
import { usersService } from "@/services/usersService";
import { pipelineAPI } from "@/services/pipelineService";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { catalogKeys, fetchUsers, fetchCompanies, fetchContacts, fetchSources, fetchCurrencies, fetchStages } from "@/hooks/queries/useCatalogs";

export default function Opportunities() {
  const { toast } = useToast();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const {
    isAdmin,
    roleId,
    filterPipelinesByPermissions,
    canViewPipeline,
    canCreateOpportunityInPipeline,
    getAccessiblePipelineIds,
  } = usePipelinePermissions();

  const getCompanySlug = () => {
    if (auth.company?.slug) {
      return auth.company.slug;
    }

    if (auth.user?.email) {
      const domain = auth.user.email.split("@")[1]?.split(".")[0];
      return domain || "default";
    }

    return "";
  };

  const companySlug = getCompanySlug();
  const companyName = auth.company?.name || "Empresa";

  const getCurrencySymbol = () => {
    if ((auth.company as any)?.currency) {
      return (auth.company as any).currency;
    }
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {
      console.error("Error reading currency from storage", e);
    }
    return "$";
  };
  const currencySymbol = getCurrencySymbol();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [allOpportunities, setAllOpportunities] = useState<Opportunity[]>([]);
  const [viewMode, setViewMode] = useState<"active" | "history">("active");

  const [allStages, setAllStages] = useState<OpportunityStage[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null);
  const [collapsedPipelines, setCollapsedPipelines] = useState<Set<string>>(
    new Set(),
  );
  const [selectedOpportunity, setSelectedOpportunity] =
    useState<Opportunity | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);
  const [isSourcesModalOpen, setIsSourcesModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [loading, setLoading] = useState({
    opportunities: false,
    stages: false,
    pipelines: false,
    preload: false,
  });
  const [preloadedData, setPreloadedData] = useState<any>(null);
  const [kanbanView, setKanbanView] = useState<"kanban" | "table">("kanban");
  const [page, setPage] = useState(1);
  const [viewingClosedType, setViewingClosedType] = useState<
    "won" | "lost" | null
  >(null);

  useEffect(() => {
    if (companySlug && auth.isAuthenticated) {
      // Si ya hay pipelines, es un refetch de paginación/vista, lo hacemos en background
      const isBackground = pipelines.length > 0;
      fetchData(isBackground);
    }
  }, [companySlug, auth.isAuthenticated, activePipelineId, viewMode, page, kanbanView]);

  useEffect(() => {
    if (companySlug && auth.isAuthenticated) {
      preloadModalData();
    }
  }, [companySlug, auth.isAuthenticated]);
  const fetchData = async (background = false) => {
    if (!companySlug) {
      toast({
        title: "Error",
        description: "No se encontró la empresa",
        variant: "destructive",
      });
      return;
    }

    if (!auth.isAuthenticated) {
      toast({
        title: "No autenticado",
        description: "Debes iniciar sesión para ver oportunidades",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!background) {
        setLoading({
          opportunities: true,
          stages: true,
          pipelines: true,
          preload: false,
        });
      }

      let accessiblePipelines = pipelines;

      // Solo cargar pipelines si no están cargados (o si no es background)
      if (!background || pipelines.length === 0) {
        const pipelinesResponse = await pipelineAPI.getPipelines({
          slug: companySlug,
          is_active: true,
        });

        const pipelinesWithPermissions = await Promise.all(
          pipelinesResponse.data.map(async (pipeline) => {
            try {
              const permissionsResponse =
                await pipelineAPI.getPipelinePermissions(
                  companySlug,
                  pipeline.id,
                );
              return {
                ...pipeline,
                permissions: permissionsResponse.data,
              };
            } catch (error) {
              console.error(
                `Error al obtener permisos del pipeline ${pipeline.id}:`,
                error,
              );
              return {
                ...pipeline,
                permissions: [],
              };
            }
          }),
        );
        accessiblePipelines = filterPipelinesByPermissions(pipelinesWithPermissions);
        setPipelines(accessiblePipelines);
      }

      if (accessiblePipelines.length === 0) {
        toast({
          title: "Sin acceso",
          description:
            "No tienes acceso a ningún pipeline. Contacta al administrador.",
          variant: "destructive",
        });
        setLoading({
          opportunities: false,
          stages: false,
          pipelines: false,
          preload: false,
        });
        return;
      }

      const pipelineIdToUse =
        activePipelineId ||
        (
          accessiblePipelines.find((p) => p.is_default) ||
          accessiblePipelines[0]
        )?.id;

      if (!activePipelineId && pipelineIdToUse) {
        setActivePipelineId(pipelineIdToUse);
      }

      let stagesData: OpportunityStage[] = allStages;
      if (pipelineIdToUse && (!background || allStages.length === 0)) {
        const stagesResponse = await opportunityAPI.getStages({
          slug: companySlug,
          pipeline_id: pipelineIdToUse,
        });
        stagesData = stagesResponse.data
          .filter((s: any) => s.pipeline_id === pipelineIdToUse)
          .sort((a: any, b: any) => a.order_position - b.order_position);

        setAllStages(stagesData);
      }

      const limit = kanbanView === "table" ? 20 : 1000;
      const isAdmin = auth.user?.role?.name === "Administrador";

      const apiParams = {
        slug: companySlug,
        page: kanbanView === "table" ? page : 1,
        limit,
        user_id: isAdmin ? undefined : (auth.user?.id as unknown as string),
        only_hidden: viewMode === "history",
      };

      const oppResponse = await queryClient.fetchQuery({
        queryKey: opportunityKeys.list(JSON.stringify(apiParams)),
        queryFn: () => opportunityAPI.getOpportunities(apiParams),
        staleTime: 1000 * 60 * 2,
      });

      const accessiblePipelineIds =
        getAccessiblePipelineIds(accessiblePipelines);

      const filterAndSetOpps = (opps: Opportunity[], pipelineId: string) =>
        opps.filter((opp) => {
          if (opp.pipeline_id !== pipelineId) return false;
          if (!accessiblePipelineIds.includes(opp.pipeline_id)) return false;
          return true;
        });

      const oppsForLog = filterAndSetOpps(
        oppResponse.data,
        pipelineIdToUse || "",
      );
      setAllOpportunities(oppsForLog);
    } catch (error: any) {
      console.error("Error al cargar datos:", error);
      toast({
        title: "Error al cargar datos",
        description: error.message || "No se pudieron cargar las oportunidades",
        variant: "destructive",
      });
    } finally {
      setLoading({
        opportunities: false,
        stages: false,
        pipelines: false,
        preload: false,
      });
    }
  };

  const preloadModalData = async () => {
    if (!companySlug || !auth.isAuthenticated) return;

    try {
      setLoading((prev) => ({ ...prev, preload: true }));

      const [
        contactsData,
        companiesData,
        stagesData,
        sourcesData,
        currenciesData,
        usersData,
      ] = await Promise.all([
        queryClient.fetchQuery({ queryKey: catalogKeys.contacts(companySlug), queryFn: () => fetchContacts(companySlug), staleTime: 1000 * 60 * 30 }),
        queryClient.fetchQuery({ queryKey: catalogKeys.companies(companySlug), queryFn: () => fetchCompanies(companySlug), staleTime: 1000 * 60 * 30 }),
        queryClient.fetchQuery({ queryKey: catalogKeys.stages(companySlug, undefined), queryFn: () => fetchStages(companySlug), staleTime: 1000 * 60 * 30 }),
        queryClient.fetchQuery({ queryKey: catalogKeys.sources(companySlug), queryFn: () => fetchSources(companySlug), staleTime: 1000 * 60 * 30 }),
        queryClient.fetchQuery({ queryKey: catalogKeys.currencies(companySlug), queryFn: () => fetchCurrencies(companySlug), staleTime: 1000 * 60 * 60 }),
        queryClient.fetchQuery({ queryKey: catalogKeys.users(companySlug), queryFn: () => fetchUsers(companySlug), staleTime: 1000 * 60 * 30 }),
      ]);

      setPreloadedData({
        contacts: contactsData,
        companies: companiesData,
        stages: stagesData,
        sources: sourcesData,
        currencies: currenciesData,
        users: usersData,
      });
    } catch (error) {
      console.error("Error preloading modal data:", error);
    } finally {
      setLoading((prev) => ({ ...prev, preload: false }));
    }
  };

  const filteredOpportunities = useMemo(() => {
    return allOpportunities;
  }, [allOpportunities, viewMode]);

  const handleOpportunityClick = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsDetailModalOpen(true);
  };

  const handleCreateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    fetchData();
  };

  const handleOpportunityUpdated = () => {
    setIsDetailModalOpen(false);
    queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    fetchData(true);
  };

  const handleEditSuccess = (newId?: string) => {
    queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    fetchData();
    // Refresh selectedOpportunity so that if the detail panel reopens it shows fresh data
    const idToFetch = newId || selectedOpportunity?.id;
    if (idToFetch && companySlug) {
      opportunityAPI.getOpportunityById(idToFetch, companySlug)
        .then((resp: any) => {
          const fresh = resp?.data;
          if (fresh) setSelectedOpportunity(fresh);
        })
        .catch(() => {/* silent */});
    }
    toast({
      title: "¡Oportunidad actualizada!",
      description: "La oportunidad se ha actualizado correctamente",
      variant: "default",
    });
  };

  const handleOpportunityDeleted = (deletedOpportunityId: string) => {
    queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    setAllOpportunities((prev) =>
      prev.filter((opp) => opp.id !== deletedOpportunityId),
    );
    fetchData();
  };

  const handleStageCreated = () => {
    queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    fetchData();
    preloadModalData();
  };

  const handleSourceCreated = () => {
    preloadModalData();
    toast({
      title: "Fuentes actualizadas",
      description: "Los cambios se reflejarán al crear nuevas oportunidades",
    });
  };

  const handleEditOpportunity = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsDetailModalOpen(false);
    setIsEditModalOpen(true);
  };

  const handleUpdateOpportunityStage = async (
    opportunityId: string,
    newStageId: string,
  ) => {
    const newStage = allStages.find((s) => s.id === newStageId);
    if (!newStage) {
      console.error("Could not find the new stage. Refetching data.");
      fetchData();
      return;
    }

    queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    setAllOpportunities((prev) =>
      prev.map((opp) =>
        opp.id === opportunityId
          ? { ...opp, stage_id: newStageId, stage: newStage }
          : opp,
      ),
    );
  };

  const handleEditStage = () => {
    setIsStageModalOpen(true);
  };

  const handleManageSources = () => {
    setIsSourcesModalOpen(true);
  };

  const handlePipelineChange = async (pipelineId: string) => {
    if (pipelineId === activePipelineId) {
      return;
    }

    const selectedPipeline = pipelines.find((p) => p.id === pipelineId);

    if (selectedPipeline && !canViewPipeline(selectedPipeline)) {
      toast({
        title: "Sin permisos",
        description: "No tienes permisos para ver este pipeline",
        variant: "destructive",
      });
      return;
    }

    setActivePipelineId(pipelineId);
    setPage(1);
    toast({
      title: "Pipeline cambiado",
      description: `Mostrando oportunidades de "${selectedPipeline?.name}"`,
    });
  };

  const handleOpenCreateModal = () => {
    if (!preloadedData) {
      toast({
        title: "Cargando datos",
        description: "Por favor espera mientras se cargan los datos necesarios",
        variant: "default",
      });
      preloadModalData();
    }
    setIsCreateModalOpen(true);
  };

  const totalValue = allOpportunities.reduce(
    (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
    0,
  );

  const wonOpportunities = allOpportunities.filter(
    (opp) => opp.stage?.stage_type === "closed_won",
  );

  const wonValue = wonOpportunities.reduce(
    (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
    0,
  );

  const lostOpportunities = allOpportunities.filter(
    (opp) => opp.stage?.stage_type === "closed_lost",
  );

  const lostValue = lostOpportunities.reduce(
    (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
    0,
  );

  const activeOpportunities = allOpportunities.filter(
    (opp) =>
      opp.stage?.stage_type !== "closed_won" &&
      opp.stage?.stage_type !== "closed_lost",
  );

  const activeValue = filteredOpportunities.reduce(
    (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
    0,
  );

  const avgDealSize =
    allOpportunities.length > 0 ? totalValue / allOpportunities.length : 0;
  const winRate =
    wonOpportunities.length + lostOpportunities.length > 0
      ? (wonOpportunities.length /
          (wonOpportunities.length + lostOpportunities.length)) *
        100
      : 0;

  const canViewOpportunities = auth.canManageOpportunities();
  const canCreateOpportunities = auth.can("opportunities.create");
  const canEditOpportunities = auth.can("opportunities.update");
  const canManageStages = auth.can("stages.manage");
  const canManageSources = auth.can("opportunity_sources.manage");

  if (!auth.isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No autenticado</AlertTitle>
          <AlertDescription>
            Debes iniciar sesión para ver las oportunidades.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!canViewOpportunities) {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Acceso denegado</AlertTitle>
          <AlertDescription>
            No tienes permisos para ver las oportunidades.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!companySlug) {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Empresa no encontrada</AlertTitle>
          <AlertDescription>
            No se pudo identificar la empresa. Contacta al administrador.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading.opportunities && !allOpportunities.length) {
    return (
      <div className="w-full min-h-[calc(100vh-6rem)] flex flex-col p-8 bg-white rounded-[32px] shadow-[0_4px_40px_-12px_rgba(0,0,0,0.06)] border border-slate-200/60">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-purple-100 p-2.5 rounded-2xl">
                <Target className="h-7 w-7 text-purple-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                Oportunidades
              </h1>
            </div>
            <p className="text-slate-500 text-sm md:text-base ml-1">
              Pipeline de ventas y oportunidades
            </p>
          </div>
        </div>
        <div className="mt-8">
          <LoadingState type="kanban" message="Cargando oportunidades..." />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[calc(100vh-6rem)] flex flex-col gap-6 p-8 bg-white rounded-[32px] shadow-[0_4px_40px_-12px_rgba(0,0,0,0.06)] border border-slate-200/60">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-purple-100 p-2.5 rounded-2xl">
              <Target className="h-7 w-7 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
              Oportunidades
              {loading.opportunities && (
                <Loader2 className="h-5 w-5 text-purple-600 animate-spin" />
              )}
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-1">
            Pipeline de ventas y oportunidades
          </p>
        </div>
      </div>

      {!companySlug && !loading.opportunities && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-amber-700">
            No se ha identificado la empresa. Por favor, inicia sesión.
          </p>
        </div>
      )}

      {companySlug && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Pipeline Activo */}
            <div className="rounded-[20px] border border-slate-100 shadow-sm bg-white p-5 hover:shadow-md transition-all flex items-center justify-between group">
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Pipeline Activo</p>
                <p className="text-2xl font-bold text-slate-800">
                  {currencySymbol}{activeValue.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-slate-400 mt-1">{activeOpportunities.length} activas</p>
              </div>
              <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg group-hover:scale-110 transition-transform">
                {currencySymbol}
              </div>
            </div>

            {/* Ganadas */}
            <div 
              className="rounded-[20px] border border-slate-100 shadow-sm bg-white p-5 hover:shadow-md transition-all flex items-center justify-between group cursor-pointer hover:border-emerald-300"
              onClick={() => setViewingClosedType("won")}
            >
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Ganadas (Historial)</p>
                <p className="text-2xl font-bold text-slate-800">
                  {currencySymbol}{wonValue.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-slate-400 mt-1">{wonOpportunities.length} ganadas &rarr;</p>
              </div>
              <div className="h-10 w-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
                <Trophy className="h-5 w-5" />
              </div>
            </div>

            {/* Perdidas */}
            <div 
              className="rounded-[20px] border border-slate-100 shadow-sm bg-white p-5 hover:shadow-md transition-all flex items-center justify-between group cursor-pointer hover:border-red-300"
              onClick={() => setViewingClosedType("lost")}
            >
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Perdidas (Historial)</p>
                <p className="text-2xl font-bold text-slate-800">
                  {currencySymbol}{lostValue.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-slate-400 mt-1">{lostOpportunities.length} perdidas &rarr;</p>
              </div>
              <div className="h-10 w-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600 group-hover:scale-110 transition-transform">
                <Trash2 className="h-5 w-5" />
              </div>
            </div>

            {/* Tasa de Éxito y Promedio */}
            <div className="rounded-[20px] border border-slate-100 shadow-sm bg-white p-5 hover:shadow-md transition-all flex items-center justify-between group">
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Tasa de Éxito</p>
                <p className="text-2xl font-bold text-slate-800">{winRate.toFixed(1)}%</p>
                <p className="text-xs text-slate-400 mt-1">
                  Prom: {currencySymbol}{avgDealSize.toLocaleString("es-ES", { maximumFractionDigits: 0 })} &bull; {allOpportunities.length} totales
                </p>
              </div>
              <div className="h-10 w-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 group-hover:scale-110 transition-transform">
                <BarChart3 className="h-5 w-5" />
              </div>
            </div>
          </div>

          <div className="bg-slate-50/80 border border-slate-200/80 rounded-[24px] shadow-sm overflow-hidden">
            <div className="p-5 px-6 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                {pipelines.length > 1 ? (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100/80 text-indigo-700 rounded-xl">
                      <Target className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-0.5">Pipeline Activo</p>
                      <Select
                        value={activePipelineId || ""}
                        onValueChange={handlePipelineChange}
                        disabled={loading.opportunities}
                      >
                        <SelectTrigger className="w-auto min-w-[200px] h-auto border-0 p-0 shadow-none bg-transparent text-xl font-bold focus:ring-0 hover:bg-slate-50 px-2 -ml-2 rounded-md">
                          <SelectValue placeholder="Seleccionar pipeline" />
                        </SelectTrigger>
                        <SelectContent>
                          {pipelines.map((pipeline) => (
                            <SelectItem key={pipeline.id} value={pipeline.id}>
                              <span className="font-medium">{pipeline.name}</span>
                              {pipeline.is_default && (
                                <span className="ml-2 text-xs text-slate-400">(Por defecto)</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100/80 text-indigo-700 rounded-xl">
                      <Target className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-slate-900">
                        {pipelines[0]?.name || "Pipeline de Oportunidades"}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">Arrastra y suelta para cambiar etapas</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                  {canCreateOpportunities && (
                    <Button
                      onClick={handleOpenCreateModal}
                      className="gap-2 bg-slate-900 hover:bg-black text-white rounded-xl shadow-sm shadow-slate-900/20 font-medium"
                      disabled={
                        loading.opportunities ||
                        loading.stages ||
                        !companySlug ||
                        loading.preload
                      }
                      size="sm"
                    >
                      {loading.preload ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      Nueva Oportunidad
                    </Button>
                  )}

                  {canManageStages && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEditStage}
                      className="gap-2 rounded-xl border-slate-200 hover:bg-slate-50"
                      disabled={loading.stages || !companySlug}
                    >
                      {loading.stages ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Settings className="h-4 w-4 mr-1" />
                      )}
                      Etapas
                    </Button>
                  )}

                  {canManageSources && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleManageSources}
                      className="gap-2 rounded-xl border-slate-200 hover:bg-slate-50"
                      disabled={!companySlug}
                    >
                      <List className="h-4 w-4 mr-1" />
                      Fuentes
                    </Button>
                  )}

                  <Button
                    variant={viewMode === "active" ? "outline" : "default"}
                    size="sm"
                    onClick={() =>
                      setViewMode(viewMode === "active" ? "history" : "active")
                    }
                    className="gap-2 rounded-xl border-slate-200"
                    title={
                      viewMode === "active"
                        ? "Ver oportunidades archivadas"
                        : "Ver oportunidades activas"
                    }
                  >
                    {viewMode === "active" ? (
                      <>
                        <EyeOff className="h-4 w-4" />
                        Historial
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        Activas
                      </>
                    )}
                  </Button>

                  <Tabs
                    value={kanbanView}
                    onValueChange={(v) =>
                      setKanbanView(v as "kanban" | "table")
                    }
                    className="flex-shrink-0"
                  >
                    <TabsList className="bg-slate-100/80 p-1.5 rounded-xl">
                      <TabsTrigger
                        value="kanban"
                        title="Vista Kanban"
                        className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm h-8 px-3"
                      >
                        <LayoutGrid className="h-4 w-4" />
                      </TabsTrigger>
                      <TabsTrigger
                        value="table"
                        title="Vista Tabla"
                        className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm h-8 px-3"
                      >
                        <LayoutList className="h-4 w-4" />
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
              </div>
            </div>
            <div className="p-4">
              {!activePipelineId ? (
                <div className="text-center py-12">
                  <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    No hay pipeline seleccionado
                  </h3>
                  <p className="text-slate-600">
                    Selecciona un pipeline para ver las oportunidades
                  </p>
                </div>
              ) : filteredOpportunities.length === 0 && kanbanView === 'kanban' ? (
                <div className="text-center py-12">
                  <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {viewMode === "history"
                      ? "El historial está vacío"
                      : "No hay oportunidades"}
                  </h3>
                  <p className="text-slate-600 mb-4">
                    {viewMode === "history"
                      ? "Aquí aparecerán las oportunidades ganadas o perdidas después de un tiempo."
                      : "Comienza creando tu primera oportunidad"}
                  </p>
                  {canCreateOpportunities && (
                    <Button
                      onClick={handleOpenCreateModal}
                      className="gap-2"
                      disabled={
                        loading.opportunities ||
                        loading.stages ||
                        !companySlug ||
                        loading.preload
                      }
                    >
                      {loading.preload ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      Crear primera oportunidad
                    </Button>
                  )}
                </div>
              ) : (
                <div className="relative">
                  {loading.opportunities && (
                    <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-50 flex items-center justify-center rounded-2xl min-h-[400px]">
                      <div className="bg-white/80 p-6 rounded-2xl shadow-lg border border-slate-100 flex flex-col items-center gap-3">
                        <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
                        <p className="text-sm font-semibold text-slate-700">Actualizando oportunidades...</p>
                      </div>
                    </div>
                  )}
                  {kanbanView === "table" ? (
                    <div className="space-y-4">
                      <OpportunityTable
                        opportunities={filteredOpportunities}
                        stages={allStages}
                        slug={companySlug}
                        onView={handleOpportunityClick}
                      />
                      <div className="flex justify-between items-center bg-white p-4 rounded-lg border">
                        <span className="text-sm text-gray-500">
                          Página {page}
                        </span>
                        <div className="space-x-2">
                          <button
                            className="px-4 py-2 border rounded-md disabled:opacity-50"
                            disabled={page === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            Anterior
                          </button>
                          <button
                            className="px-4 py-2 border rounded-md disabled:opacity-50"
                            disabled={filteredOpportunities.length < 20}
                            onClick={() => setPage((p) => p + 1)}
                          >
                            Siguiente
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <OpportunityKanban
                      slug={companySlug}
                      opportunities={filteredOpportunities}
                      stages={allStages}
                      onOpportunityClick={handleOpportunityClick}
                      onCreateOpportunity={handleOpenCreateModal}
                      onStageEdit={handleEditStage}
                      onStageChange={handleUpdateOpportunityStage}
                      view={kanbanView}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {canCreateOpportunities && (
        <CreateOpportunityModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          slug={companySlug}
          pipelineId={activePipelineId || undefined}
          preloadedData={preloadedData}
        />
      )}

      {canEditOpportunities && (
        <EditOpportunityModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={handleEditSuccess}
          slug={companySlug}
          opportunity={selectedOpportunity}
        />
      )}

      {canManageStages && (
        <CreateStageModal
          isOpen={isStageModalOpen}
          onClose={() => setIsStageModalOpen(false)}
          slug={companySlug}
          pipelineId={activePipelineId || undefined}
          onStageCreated={handleStageCreated}
          onStageUpdated={handleStageCreated}
          onStageDeleted={handleStageCreated}
        />
      )}

      <OpportunitySourcesModal
        isOpen={isSourcesModalOpen}
        onClose={() => setIsSourcesModalOpen(false)}
        slug={companySlug}
        onSourceCreated={handleSourceCreated}
      />

      <OpportunityDetail
        opportunityId={selectedOpportunity?.id || null}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        slug={companySlug}
        onEdit={handleEditOpportunity}
        onOpportunityUpdated={handleOpportunityUpdated}
        onOpportunityDeleted={handleOpportunityDeleted}
      />
      {/* Modal para ver Ganadas/Perdidas */}
      <Dialog
        open={viewingClosedType !== null}
        onOpenChange={(open) => !open && setViewingClosedType(null)}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-4">
          <DialogHeader className="mb-2">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              {viewingClosedType === "won" ? (
                <>
                  <Trophy className="h-6 w-6 text-emerald-600" />
                  Historial de Oportunidades Ganadas
                </>
              ) : (
                <>
                  <Trash2 className="h-6 w-6 text-red-600" />
                  Basurero de Oportunidades Perdidas
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 border-t border-slate-100 pt-4">
            <OpportunityTable
              slug={companySlug}
              opportunities={
                viewingClosedType === "won"
                  ? wonOpportunities
                  : lostOpportunities
              }
              stages={allStages}
              onView={(opp) => {
                setViewingClosedType(null);
                handleOpportunityClick(opp);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
